/**
 * PhaseContext - State Management für Spielphasen
 *
 * Verwaltet den aktuellen Phasenzustand und ermöglicht
 * kontrollierte Phasenübergänge.
 */

import React, {
  createContext,
  useContext,
  useCallback,
  useMemo,
  useEffect,
  useRef
} from 'react';
import { useGameState, useGameDispatch } from './GameContext';
import { useFeatureFlags } from '../features/FeatureProvider';
import {
  PHASES,
  canTransition,
  getNextPhase,
  getPhaseMeta,
  isVictoryPhase,
  isMainGamePhase,
} from '../phases/PhaseRegistry';

// Context
const PhaseContext = createContext(null);

/**
 * PhaseProvider Component
 */
export function PhaseProvider({ children, onPhaseChange }) {
  const gameState = useGameState() || {};
  const dispatch = useGameDispatch();
  const { isFeatureEnabled } = useFeatureFlags();

  // Ref für vorherige Phase (für Callbacks)
  const previousPhaseRef = useRef(gameState.phase || 'lobby');

  // Aktuelle Phase aus Game State (mit Fallback)
  const currentPhase = gameState.phase || PHASES.LOBBY;

  // Phase-Metadaten
  const phaseMeta = useMemo(() => getPhaseMeta(currentPhase), [currentPhase]);

  /**
   * Prüft ob Übergang zu einer Phase erlaubt ist
   */
  const canTransitionTo = useCallback((targetPhase) => {
    // Prüfe State-Machine-Regeln
    if (!canTransition(currentPhase, targetPhase)) {
      return false;
    }

    // Prüfe Feature-Flags
    const meta = getPhaseMeta(targetPhase);
    if (meta.featureFlag && !isFeatureEnabled(meta.featureFlag)) {
      return false;
    }

    return true;
  }, [currentPhase, isFeatureEnabled]);

  /**
   * Führt einen Phasenübergang durch
   */
  const transitionTo = useCallback((targetPhase, data = {}) => {
    if (!canTransitionTo(targetPhase)) {
      console.warn(`Phase transition not allowed: ${currentPhase} -> ${targetPhase}`);
      return false;
    }

    // Dispatch Phase-Änderung
    dispatch({
      type: 'SET_PHASE',
      payload: targetPhase,
    });

    // Optional: Zusätzliche Daten speichern
    if (data && Object.keys(data).length > 0) {
      dispatch({
        type: 'GAME_STATE_UPDATE',
        payload: data,
      });
    }

    return true;
  }, [currentPhase, canTransitionTo, dispatch]);

  /**
   * Geht zur nächsten Phase im Standard-Flow
   */
  const advanceToNextPhase = useCallback((context = {}) => {
    // Feature-Flags in Context einbeziehen
    const enrichedContext = {
      ...context,
      skipPreGameShop: !isFeatureEnabled('PRE_GAME_SHOP'),
      hasExchange: isFeatureEnabled('CARD_EXCHANGE'),
    };

    let nextPhase = getNextPhase(currentPhase, enrichedContext);

    // Skip deaktivierte Phasen
    const meta = getPhaseMeta(nextPhase);
    if (meta.featureFlag && !isFeatureEnabled(meta.featureFlag)) {
      // Rekursiv nächste Phase finden
      nextPhase = getNextPhase(nextPhase, enrichedContext);
    }

    return transitionTo(nextPhase, context.data);
  }, [currentPhase, isFeatureEnabled, transitionTo]);

  /**
   * Callback bei Phasenwechsel
   */
  useEffect(() => {
    if (previousPhaseRef.current !== currentPhase) {
      const prevPhase = previousPhaseRef.current;
      previousPhaseRef.current = currentPhase;

      // Callback aufrufen
      if (onPhaseChange) {
        onPhaseChange({
          from: prevPhase,
          to: currentPhase,
          meta: phaseMeta,
        });
      }
    }
  }, [currentPhase, phaseMeta, onPhaseChange]);

  /**
   * Auto-Advance für Phasen mit Timer
   */
  useEffect(() => {
    if (phaseMeta.autoAdvance && phaseMeta.autoAdvanceDelay) {
      const timer = setTimeout(() => {
        advanceToNextPhase();
      }, phaseMeta.autoAdvanceDelay);

      return () => clearTimeout(timer);
    }
  }, [currentPhase, phaseMeta, advanceToNextPhase]);

  // Context Value
  const value = useMemo(() => ({
    // Aktuelle Phase
    currentPhase,
    phaseMeta,

    // Phasen-Checks
    canTransitionTo,
    isVictoryPhase: isVictoryPhase(currentPhase),
    isMainGamePhase: isMainGamePhase(currentPhase),
    isInGame: isMainGamePhase(currentPhase) || isVictoryPhase(currentPhase),

    // Phasen-Aktionen
    transitionTo,
    advanceToNextPhase,

    // Shortcuts für häufige Übergänge
    goToLobby: () => {
      const target = {
        lobby: PHASES.LOBBY,
        quickPlay: PHASES.QUICK_PLAY,
        stammtisch: PHASES.STAMMTISCH,
      }[gameState.lobbyOrigin] || PHASES.START_SCREEN;
      return transitionTo(target);
    },
    startGame: () => {
      const target = isFeatureEnabled('PRE_GAME_SHOP')
        ? PHASES.PRE_GAME_SHOP
        : PHASES.PLAYING;
      return transitionTo(target);
    },

    // Konstanten
    PHASES,
  }), [
    currentPhase,
    phaseMeta,
    canTransitionTo,
    transitionTo,
    advanceToNextPhase,
    isFeatureEnabled,
    gameState.lobbyOrigin,
  ]);

  return (
    <PhaseContext.Provider value={value}>
      {children}
    </PhaseContext.Provider>
  );
}

/**
 * Hook zum Zugriff auf Phase-Context
 */
export function usePhase() {
  const context = useContext(PhaseContext);
  if (!context) {
    throw new Error('usePhase must be used within a PhaseProvider');
  }
  return context;
}

/**
 * Hook der nur die aktuelle Phase zurückgibt
 */
export function useCurrentPhase() {
  const { currentPhase } = usePhase();
  return currentPhase;
}

/**
 * Hook der prüft ob wir in einer bestimmten Phase sind
 */
export function useIsPhase(phase) {
  const { currentPhase } = usePhase();
  return currentPhase === phase;
}

/**
 * Hook für Phase-Metadaten
 */
export function usePhaseMeta() {
  const { phaseMeta } = usePhase();
  return phaseMeta;
}

export default PhaseProvider;
